/**
 * GenUI API Service
 * Handles API calls for the GenUI chat-based AI interface.
 * Uses fetch directly for SSE streaming (not the ApiClient singleton).
 */

import type {
  GenUIChatRequest,
  GenUIConversation,
  GenUIUsageStats,
  GenUIFileAttachment,
} from '@/types/genui';
import { ApiError } from '../client';

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const BASE = '/api/v1/genui';

/**
 * Send a chat message and get a streaming SSE response.
 * Returns a ReadableStream for progressive rendering.
 */
export async function streamChat(
  request: GenUIChatRequest,
  signal?: AbortSignal
): Promise<Response> {
  const response = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...request, stream: true }),
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return response;
}

/**
 * Send a chat message and get a non-streaming JSON response.
 */
export async function sendChatMessage(
  request: GenUIChatRequest
): Promise<{
  success: boolean;
  data?: { content: string; toolResults: unknown[]; conversationId: string };
  error?: string;
}> {
  const response = await fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ ...request, stream: false }),
  });

  return response.json();
}

/**
 * List user's GenUI conversations.
 */
export async function listConversations(params?: {
  limit?: number;
  pageToken?: string;
}): Promise<{
  success: boolean;
  data: GenUIConversation[];
  pageToken?: string;
}> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.pageToken) queryParams.set('pageToken', params.pageToken);

  const qs = queryParams.toString();
  const response = await fetch(`${BASE}/conversations${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });

  return response.json();
}

/**
 * Get messages for a specific conversation.
 */
export async function getConversationMessages(
  conversationId: string,
  params?: { limit?: number; pageToken?: string }
): Promise<{
  success: boolean;
  data: unknown[];
  pageToken?: string;
}> {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.pageToken) queryParams.set('pageToken', params.pageToken);

  const qs = queryParams.toString();
  const response = await fetch(
    `${BASE}/conversations/${conversationId}${qs ? `?${qs}` : ''}`,
    { headers: getAuthHeaders() }
  );

  return response.json();
}

/**
 * Archive (soft-delete) a conversation.
 */
export async function archiveConversation(
  conversationId: string
): Promise<{ success: boolean }> {
  const response = await fetch(`${BASE}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  return response.json();
}

/**
 * Upload a file for use in GenUI conversations.
 * Returns file metadata including S3 URL for reference in subsequent messages.
 */
export async function uploadFile(
  file: File,
  businessAccountId: string
): Promise<{ success: boolean; data?: GenUIFileAttachment; error?: string }> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('businessAccountId', businessAccountId);

  const response = await fetch(`${BASE}/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      // Don't set Content-Type — browser sets it with boundary for multipart
    },
    body: formData,
  });

  return response.json();
}

/**
 * Transcribe an audio blob using the backend Whisper endpoint.
 * Returns the transcript string.
 */
export async function transcribeAudio(blob: Blob): Promise<{ transcript: string; detectedLanguage: string | null }> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  const ext = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('ogg') ? 'ogg' : 'webm';
  formData.append('audio', blob, `recording.${ext}`);
  // Do NOT send a language hint — let Whisper auto-detect from the audio.
  // Sending navigator.language (usually "en") forces English transcription
  // even when the user speaks Telugu, Hindi, etc.

  const response = await fetch(`${BASE}/transcribe`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  const data = await response.json();
  if (!data.success) throw ApiError.fromResult({ ...data, statusCode: response.status }, 'Transcription failed');
  return { transcript: data.transcript as string, detectedLanguage: data.detectedLanguage ?? null };
}

/**
 * Get GenUI token usage statistics.
 */
export async function getUsageStats(params?: {
  startDate?: string;
  endDate?: string;
}): Promise<{
  success: boolean;
  data: GenUIUsageStats;
}> {
  const queryParams = new URLSearchParams();
  if (params?.startDate) queryParams.set('startDate', params.startDate);
  if (params?.endDate) queryParams.set('endDate', params.endDate);

  const qs = queryParams.toString();
  const response = await fetch(`${BASE}/usage${qs ? `?${qs}` : ''}`, {
    headers: getAuthHeaders(),
  });

  return response.json();
}
